import { useState, createContext, useEffect, useRef } from "react";
import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import { GroupIconNotification, LanguageIcon, UserIcon } from "@/icons";
import { toast } from "react-toastify";
import RoomService from "@/services/room.service";
import UserService from "@/services/user.service";
import NotificationService from "@/services/notification.service";
import { useTranslation } from "react-i18next";

// Tab Components
const CreateForAllTab = () => {
    const { t } = useTranslation();
    const [notificationData, setNotificationData] = useState({
        notification: "",
        type: "All"
    });

    const [isLoading, setIsLoading] = useState(false);
    const validate = () => {
        if (!notificationData.notification.trim()) return t("Notification is required.");
        return null;
    };
    // Handle Generate
    const handlePush = async () => {
        setIsLoading(true);
        const validationError = validate();
        if (validationError) {
            toast.error(validationError);
            setIsLoading(false);
            return;
        }
        try {
            const response: any = await NotificationService.createMultipleNotification(notificationData);
            if (response.data.status === 200) {
                toast.success(t("Notification pushed successfully!"));
                // setTimeout(() => {
                //     window.location.href = "/";
                // }, 1500);
            }
        } catch (error) {
            console.error("Error creating deck:", error);
            toast.error(t("Failed to push notification. Please try again."));
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <Label>{t("Notification")}</Label>
                <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder={t("Enter notification content")}
                    rows={4}
                    value={notificationData.notification}
                    onChange={(e) =>
                        setNotificationData({
                            ...notificationData,
                            notification: e.target.value,
                        })
                    }
                />
            </div>

            <button onClick={handlePush} disabled={isLoading} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors">
                {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5 animate-spin mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t("Pushing")}...
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-4 h-4"
                        >
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                        {t("Push")}
                    </div>
                )}
            </button>
        </div>
    );
};

const CreateByRoomTab = () => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: "",
    });

    const [rooms, setRooms] = useState<any[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [notificationData, setNotificationData] = useState<{
        rooms: { _id: string; room_name: string }[];
        notification: string;
        type: string;
    }>({
        rooms: [],
        notification: "",
        type: "Room",
    });

    const [isLoading, setIsLoading] = useState(false);

    const validate = () => {
        if (notificationData.rooms.length === 0) return t("Room is required.");
        if (!notificationData.notification.trim())
            return t("Notification is required.");
        return null;
    };

    const fetchRooms = async (isLoadMore = false) => {
        try {
            const response = await RoomService.getListForAdmin(params);
            const newRooms = response.data.rooms || [];

            setRooms((prev) =>
                isLoadMore ? [...prev, ...newRooms] : newRooms
            );

            if (newRooms.length < params.limit) {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Error fetching rooms:", err);
        }
    };

    useEffect(() => {
        fetchRooms(params.page > 1);
    }, [params]);

    // click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlePush = async () => {
        setIsLoading(true);
        const error = validate();
        if (error) {
            toast.error(error);
            setIsLoading(false);
            return;
        }
        try {
            const response: any = await NotificationService.createMultipleNotification({
                ...notificationData,
                rooms: notificationData.rooms.map((room) => room._id),
            });
            if (response.data.status === 200) {
                toast.success(t("Notification pushed successfully!"));
                // setTimeout(() => {
                //     window.location.href = "/";
                // }, 1500);
            }
        } catch (error) {
            console.error("Error creating deck:", error);
            toast.error(t("Failed to push notification. Please try again."));
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Select Room */}
            <div ref={dropdownRef} className="relative">
                <Label>{t("Select Room")}</Label>

                <div
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full p-3 border border-gray-300 rounded-lg cursor-pointer bg-white"
                >
                    {notificationData.rooms.length > 0
                        ? `${notificationData.rooms.length} ${t("room(s)")} ${t("selected")}`
                        : t("Select rooms")}
                </div>

                {isOpen && (
                    <div className="absolute z-20 mt-2 w-full bg-white border rounded-lg shadow-lg">
                        {/* Search */}
                        <div className="relative p-2">
                            <svg
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35m1.85-5.65a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
                                />
                            </svg>

                            <input
                                className="w-full pl-10 pr-3 py-2 border rounded-full outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={t("Search room") + "..."}
                                value={params.search}
                                onChange={(e) => {
                                    setParams({
                                        ...params,
                                        page: 1,
                                        search: e.target.value,
                                    });
                                    setHasMore(true);
                                }}
                            />
                        </div>

                        {/* List */}
                        <div
                            className="max-h-60 overflow-y-auto"
                            onScroll={(e) => {
                                const target = e.currentTarget;
                                if (
                                    target.scrollTop + target.clientHeight >=
                                    target.scrollHeight - 10 &&
                                    hasMore
                                ) {
                                    setParams((prev) => ({
                                        ...prev,
                                        page: prev.page + 1,
                                    }));
                                }
                            }}
                        >
                            {rooms.map((room) => {
                                const isSelected = notificationData.rooms.some(
                                    (r) => r._id === room._id
                                );

                                return (
                                    <div
                                        key={room._id}
                                        onClick={() => {
                                            if (isSelected) return;

                                            setNotificationData((prev) => ({
                                                ...prev,
                                                rooms: [
                                                    ...prev.rooms,
                                                    {
                                                        _id: room._id,
                                                        room_name: room.room_name,
                                                    },
                                                ],
                                            }));
                                        }}
                                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex justify-between ${isSelected
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                            }`}
                                    >
                                        <span>{room.room_name}</span>
                                        {isSelected && (
                                            <span className="text-green-500">✓</span>
                                        )}
                                    </div>
                                );
                            })}

                            {hasMore && (
                                <div className="p-2 text-center text-sm text-gray-400">
                                    {t("Loading more")}...
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Selected rooms */}
            <div className="flex flex-wrap gap-2">
                {notificationData.rooms.map((room) => (
                    <div
                        key={room._id}
                        className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                        <span>{room.room_name}</span>
                        <button
                            onClick={() =>
                                setNotificationData((prev) => ({
                                    ...prev,
                                    rooms: prev.rooms.filter(
                                        (r) => r._id !== room._id
                                    ),
                                }))
                            }
                            className="hover:text-red-500 transition"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-4 h-4"
                            >
                                <path d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7A1 1 0 105.7 7.11L10.59 12l-4.9 4.89a1 1 0 101.41 1.42L12 13.41l4.89 4.9a1 1 0 001.42-1.41L13.41 12l4.9-4.89a1 1 0 000-1.4z" />
                            </svg>
                        </button>
                    </div>
                ))}
            </div>

            {/* Notification */}
            <div>
                <Label>{t("Notification")}</Label>
                <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={4}
                    placeholder={t("Enter notification content")}
                    value={notificationData.notification}
                    onChange={(e) =>
                        setNotificationData({
                            ...notificationData,
                            notification: e.target.value,
                        })
                    }
                />
            </div>
            <button onClick={handlePush} disabled={isLoading} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors">
                {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t("Pushing")}...
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-4 h-4"
                        >
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                        {t("Push")}
                    </div>
                )}
            </button>
        </div>
    );
};

const CreateByUserTab = () => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: "",
    });

    const [users, setUsers] = useState<any[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [notificationData, setNotificationData] = useState<{
        users: { _id: string; full_name: string }[];
        notification: string;
        type: string;
    }>({
        users: [],
        notification: "",
        type: "User"
    });

    const [isLoading, setIsLoading] = useState(false);

    const validate = () => {
        if (notificationData.users.length === 0) return t("User is required.");
        if (!notificationData.notification.trim())
            return t("Notification is required.");
        return null;
    };

    const fetchUsers = async (isLoadMore = false) => {
        try {
            const response = await UserService.getUsers(params);
            const newUsers = response.data.data || [];

            setUsers((prev) =>
                isLoadMore ? [...prev, ...newUsers] : newUsers
            );

            if (newUsers.length < params.limit) {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Error fetching rooms:", err);
        }
    };

    useEffect(() => {
        fetchUsers(params.page > 1);
    }, [params]);

    // click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlePush = async () => {
        setIsLoading(true);
        const error = validate();
        if (error) {
            toast.error(error);
            setIsLoading(false);
            return;
        }
        try {
            const response: any = await NotificationService.createMultipleNotification({
                ...notificationData,
                users: notificationData.users.map((user) => user._id),
            });
            if (response.data.status === 200) {
                toast.success(t("Notification pushed successfully!"));
                // setTimeout(() => {
                //     window.location.href = "/";
                // }, 1500);
            }
        } catch (error) {
            console.error("Error creating deck:", error);
            toast.error(t("Failed to push notification. Please try again."));
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Select Room */}
            <div ref={dropdownRef} className="relative">
                <Label>{t("Select User")}</Label>

                <div
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full p-3 border border-gray-300 rounded-lg cursor-pointer bg-white"
                >
                    {notificationData.users.length > 0
                        ? `${notificationData.users.length} ${t("user(s)")} ${t("selected")}`
                        : t("Select users")}
                </div>

                {isOpen && (
                    <div className="absolute z-20 mt-2 w-full bg-white border rounded-lg shadow-lg">
                        {/* Search */}
                        <div className="relative p-2">
                            <svg
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35m1.85-5.65a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
                                />
                            </svg>

                            <input
                                className="w-full pl-10 pr-3 py-2 border rounded-full outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={t("Search user") + "..."}
                                value={params.search}
                                onChange={(e) => {
                                    setParams({
                                        ...params,
                                        page: 1,
                                        search: e.target.value,
                                    });
                                    setHasMore(true);
                                }}
                            />
                        </div>

                        {/* List */}
                        <div
                            className="max-h-60 overflow-y-auto"
                            onScroll={(e) => {
                                const target = e.currentTarget;
                                if (
                                    target.scrollTop + target.clientHeight >=
                                    target.scrollHeight - 10 &&
                                    hasMore
                                ) {
                                    setParams((prev) => ({
                                        ...prev,
                                        page: prev.page + 1,
                                    }));
                                }
                            }}
                        >
                            {users.map((user) => {
                                const isSelected = notificationData.users.some(
                                    (u) => u._id === user._id
                                );

                                return (
                                    <div
                                        key={user._id}
                                        onClick={() => {
                                            if (isSelected) return;

                                            setNotificationData((prev) => ({
                                                ...prev,
                                                users: [
                                                    ...prev.users,
                                                    {
                                                        _id: user._id,
                                                        full_name: user.full_name,
                                                    },
                                                ],
                                            }));
                                        }}
                                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex justify-between ${isSelected
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                            }`}
                                    >
                                        <span>{user.full_name}</span>
                                        {isSelected && (
                                            <span className="text-green-500">✓</span>
                                        )}
                                    </div>
                                );
                            })}

                            {hasMore && (
                                <div className="p-2 text-center text-sm text-gray-400">
                                    {t("Loading more")}...
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Selected rooms */}
            <div className="flex flex-wrap gap-2">
                {notificationData.users.map((user) => (
                    <div
                        key={user._id}
                        className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                        <span>{user.full_name}</span>
                        <button
                            onClick={() =>
                                setNotificationData((prev) => ({
                                    ...prev,
                                    users: prev.users.filter(
                                        (u) => u._id !== user._id
                                    ),
                                }))
                            }
                            className="hover:text-red-500 transition"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-4 h-4"
                            >
                                <path d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7A1 1 0 105.7 7.11L10.59 12l-4.9 4.89a1 1 0 101.41 1.42L12 13.41l4.89 4.9a1 1 0 001.42-1.41L13.41 12l4.9-4.89a1 1 0 000-1.4z" />
                            </svg>
                        </button>
                    </div>
                ))}
            </div>

            {/* Notification */}
            <div>
                <Label>{t("Notification")}</Label>
                <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={4}
                    placeholder={t("Enter notification content")}
                    value={notificationData.notification}
                    onChange={(e) =>
                        setNotificationData({
                            ...notificationData,
                            notification: e.target.value,
                        })
                    }
                />
            </div>
            <button onClick={handlePush} disabled={isLoading} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors">
                {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t("Pushing")}...
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-4 h-4"
                        >
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                        {t("Push")}
                    </div>
                )}
            </button>
        </div>
    );
};

export default function CreatePushNotification() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('All');

    const tabs = [
        { id: 'All', name: t('All'), icon: LanguageIcon },
        { id: 'Room', name: t('Room'), icon: GroupIconNotification },
        { id: 'User', name: t('User'), icon: UserIcon }
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'All':
                return <CreateForAllTab />;
            case 'Room':
                return <CreateByRoomTab />;
            case 'User':
                return <CreateByUserTab />;
            default:
                return <CreateForAllTab />;
        }
    };

    return (
        <div>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description=""
            />
            <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
                <div className="space-y-6">
                    <ComponentCard title={t("Create Push Notification")}>
                        <div className="space-y-6">
                            {/* Tab Navigation */}
                            <div className="border-b border-gray-200">
                                <nav className="-mb-px flex space-x-8">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === tab.id
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            <img src={tab.icon} alt={tab.name} className="w-5 h-5 object-contain invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[90%] contrast-[90%]" />
                                            {tab.name}
                                        </button>
                                    ))}
                                </nav>
                            </div>

                            {/* Tab Content */}
                            <div>
                                {renderTabContent()}
                            </div>
                        </div>
                    </ComponentCard>
                </div>
            </div>
        </div>
    );
}